import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2Icon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import type { Conversation, Message, PartialConversation } from '../../types';
import { groupMessagesByDate } from '../../utils';
import { ChatBoxMessageBubble } from './ChatBoxMessageBubble';

/**
 * The conversation messages in a chat box.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 * @see {@link ChatBox}
 */
export function ChatBoxMessages({
    className,
    conversation,
    messages,
    isLoading = false,
}: {
    className?: string;
    conversation?: Conversation | PartialConversation;
    messages: Message[];
    isLoading?: boolean;
}) {
    const [isUserScolled, setIsUserScolled] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const allMessages = useMemo(() => {
        if (isLoading && conversation && 'LatestMessage' in conversation) {
            // The conversation is not loaded yet, but there may be optimisitic messages.
            const optimisticMessages = [...messages];
            const latestMessage = conversation.LatestMessage;
            const isLatestMessageInMessages = messages.some(
                (message) => message.ID === latestMessage.ID
            );
            if (!isLatestMessageInMessages) {
                optimisticMessages.push(latestMessage);
            }
            return optimisticMessages;
        }
        // The conversation is loaded or there is no latest message.
        return messages;
    }, [conversation, messages, isLoading]);
    const groupedMessages = useMemo(() => {
        return groupMessagesByDate(allMessages);
    }, [allMessages]);

    // Scroll to the bottom when the messages are updated (except when the user
    // has manually scrolled up).
    useEffect(() => {
        if (!isUserScolled) {
            containerRef.current?.scrollTo({
                top: containerRef.current.scrollHeight,
                behavior: 'instant',
            });
        }
    }, [isUserScolled, allMessages]);

    return (
        <div
            ref={containerRef}
            className={cn(
                'flex select-text flex-col gap-[8px] overflow-y-auto p-[12px]',
                className
            )}
            onScroll={(e) => {
                const distanceToBottom = Math.abs(
                    e.currentTarget.scrollHeight -
                        e.currentTarget.scrollTop -
                        e.currentTarget.clientHeight
                );
                setIsUserScolled(distanceToBottom >= 100);
            }}
        >
            {isLoading && (
                <Loader2Icon className="mx-auto size-[32px] shrink-0 animate-spin text-gray-600" />
            )}
            {groupedMessages.map(({ dateString, messages }) => (
                <React.Fragment key={dateString}>
                    <div className="mx-auto my-[8px] w-fit min-w-[112px] rounded-full bg-gray-400 px-[12px] py-[2px] text-center text-[12px] leading-normal text-gray-500 text-white">
                        {dateString}
                    </div>
                    {messages.map((message) => (
                        <ChatBoxMessageBubble
                            key={message.ID}
                            message={message}
                        />
                    ))}
                </React.Fragment>
            ))}
        </div>
    );
}
